import AssistantReportService from "@/services/AssistantReportService";

const SAVED_CONTEXTS_KEY = "assistant_report_contexts";
const SAVED_REPORTS_KEY = "assistant_report_saved_reports";

function readStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
}

function writeStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value || []));
}

function buildDebugEvent(event) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    at: new Date().toISOString(),
    type: event?.type || "info",
    title: event?.title || "Evento",
    summary: event?.summary || "",
    payload: event?.payload || {},
  };
}

function buildDefaultRequest() {
  const today = new Date();
  const dateInitial = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    prompt: "",
    context: "",
    reportType: "",
    execute: false,
    dateInitial,
    dateFinal: today,
    models: ["55", "65"],
    sources: [],
    messages: [],
    filters: [],
    confirmedPlan: null,
  };
}

function normalizeRequest(request) {
  return {
    ...buildDefaultRequest(),
    ...(request || {}),
  };
}

export const assistantReports = {
  namespaced: true,
  state: {
    loading: false,
    error: null,
    currentRequest: buildDefaultRequest(),
    currentPreview: null,
    currentReport: null,
    debugEvents: [],
    savedContexts: readStorage(SAVED_CONTEXTS_KEY),
    savedReports: readStorage(SAVED_REPORTS_KEY),
  },
  actions: {
    async generatePreview({ commit }, payload) {
      const request = normalizeRequest(payload);

      commit("setLoading", true);
      commit("setError", null);
      commit("setCurrentRequest", request);
      commit(
        "appendDebugEvent",
        buildDebugEvent({
          type: "request",
          title: "Geração de prévia do relatório",
          summary: request.prompt || "Solicitação sem texto",
          payload: request,
        })
      );

      try {
        const result = await AssistantReportService.GeneratePreview({
          prompt: request.prompt,
          context: request.context,
          reportType: request.reportType,
          dateInitial: request.dateInitial,
          dateFinal: request.dateFinal,
          models: request.models,
          sources: request.sources,
          messages: request.messages,
          filters: request.filters,
        });

        if (result?.Success === true) {
          commit("setCurrentPreview", result.Data || null);
          commit("setCurrentReport", null);
          commit("setCurrentRequest", {
            ...request,
            dateInitial:
              result?.Data?.dateInitial ?? request.dateInitial ?? null,
            dateFinal: result?.Data?.dateFinal ?? request.dateFinal ?? null,
            confirmedPlan:
              result?.Data?.confirmedPlan || request.confirmedPlan || null,
          });
        } else {
          commit(
            "setError",
            result?.Message || "Não foi possível gerar a prévia do relatório."
          );
        }

        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: result?.Success === true ? "response" : "warning",
            title: "Retorno da prévia do relatório",
            summary: result?.Message || "Resposta recebida",
            payload: result,
          })
        );

        return Promise.resolve(result);
      } catch (error) {
        const message =
          error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Não foi possível gerar a prévia do relatório.";
        commit("setError", message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "error",
            title: "Falha ao gerar prévia",
            summary: message,
            payload: error?.response?.data || error,
          })
        );
        return Promise.reject(error?.response?.data || error);
      } finally {
        commit("setLoading", false);
      }
    },
    async generateReport({ commit }, payload) {
      const request = normalizeRequest(payload);

      commit("setLoading", true);
      commit("setError", null);
      commit("setCurrentRequest", request);
      commit(
        "appendDebugEvent",
        buildDebugEvent({
          type: "request",
          title: "Geração do relatório",
          summary: request.prompt || "Solicitação sem texto",
          payload: request,
        })
      );

      try {
        const result = await AssistantReportService.Generate({
          prompt: request.prompt,
          context: request.context,
          reportType: request.reportType,
          dateInitial: request.dateInitial,
          dateFinal: request.dateFinal,
          models: request.models,
          sources: request.sources,
          messages: request.messages,
          filters: request.filters,
          confirmedPlan: request.confirmedPlan || null,
        });

        if (result?.Success === true) {
          commit("setCurrentPreview", result.Data || null);
          commit(
            "setCurrentReport",
            result?.Data?.status === "ready" ? result.Data : null
          );
          commit("setCurrentRequest", {
            ...request,
            dateInitial:
              result?.Data?.dateInitial ?? request.dateInitial ?? null,
            dateFinal: result?.Data?.dateFinal ?? request.dateFinal ?? null,
            confirmedPlan:
              result?.Data?.confirmedPlan || request.confirmedPlan || null,
          });
        } else {
          commit(
            "setError",
            result?.Message || "Não foi possível gerar o relatório."
          );
        }

        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: result?.Success === true ? "response" : "warning",
            title: "Retorno da geração do relatório",
            summary:
              result?.Data?.status ||
              result?.Message ||
              "Resposta recebida do backend",
            payload: result,
          })
        );

        return Promise.resolve(result);
      } catch (error) {
        const message =
          error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Não foi possível gerar o relatório.";
        commit("setError", message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "error",
            title: "Falha ao gerar relatório",
            summary: message,
            payload: error?.response?.data || error,
          })
        );
        return Promise.reject(error?.response?.data || error);
      } finally {
        commit("setLoading", false);
      }
    },
    async refreshReport({ commit, state }, payload) {
      const request = normalizeRequest({
        ...(state.currentRequest || {}),
        ...(payload || {}),
      });

      commit("setLoading", true);
      commit("setError", null);
      commit("setCurrentRequest", request);
      commit(
        "appendDebugEvent",
        buildDebugEvent({
          type: "request",
          title: "Atualização do relatório",
          summary: request.prompt || "Atualização sem texto",
          payload: request,
        })
      );

      try {
        const result = await AssistantReportService.Refresh({
          prompt: request.prompt,
          context: request.context,
          reportType: request.reportType,
          dateInitial: request.dateInitial,
          dateFinal: request.dateFinal,
          models: request.models,
          sources: request.sources,
          messages: request.messages,
          filters: request.filters,
          confirmedPlan:
            request.confirmedPlan ||
            state.currentPreview?.confirmedPlan ||
            state.currentReport?.confirmedPlan ||
            null,
        });

        if (result?.Success === true) {
          commit("setCurrentPreview", result.Data || null);
          commit(
            "setCurrentReport",
            result?.Data?.status === "ready" ? result.Data : state.currentReport
          );
          commit("setCurrentRequest", {
            ...request,
            dateInitial:
              result?.Data?.dateInitial ?? request.dateInitial ?? null,
            dateFinal: result?.Data?.dateFinal ?? request.dateFinal ?? null,
            confirmedPlan:
              result?.Data?.confirmedPlan ||
              request.confirmedPlan ||
              state.currentPreview?.confirmedPlan ||
              state.currentReport?.confirmedPlan ||
              null,
          });
        } else {
          commit(
            "setError",
            result?.Message || "Não foi possível atualizar o relatório."
          );
        }

        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: result?.Success === true ? "response" : "warning",
            title: "Retorno da atualização do relatório",
            summary:
              result?.Data?.status ||
              result?.Message ||
              "Resposta recebida do backend",
            payload: result,
          })
        );

        return Promise.resolve(result);
      } catch (error) {
        const message =
          error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Não foi possível atualizar o relatório.";
        commit("setError", message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "error",
            title: "Falha ao atualizar relatório",
            summary: message,
            payload: error?.response?.data || error,
          })
        );
        return Promise.reject(error?.response?.data || error);
      } finally {
        commit("setLoading", false);
      }
    },
    setDraftRequest({ commit }, payload) {
      commit("setCurrentRequest", payload || buildDefaultRequest());
      commit("setCurrentPreview", null);
      commit("setCurrentReport", null);
    },
    clearCurrentResult({ commit }) {
      commit("setCurrentPreview", null);
      commit("setCurrentReport", null);
    },
    loadSavedData({ commit }) {
      commit("setSavedContexts", readStorage(SAVED_CONTEXTS_KEY));
      commit("setSavedReports", readStorage(SAVED_REPORTS_KEY));
    },
    clearDebugEvents({ commit }) {
      commit("setDebugEvents", []);
    },
    saveContext({ commit, state }, payload) {
      const contextToSave = {
        id: Date.now(),
        name: payload?.name || `Contexto ${state.savedContexts.length + 1}`,
        prompt: payload?.prompt || state.currentRequest?.prompt || "",
        context: payload?.context || state.currentRequest?.context || "",
        dateInitial:
          payload?.dateInitial || state.currentRequest?.dateInitial || null,
        dateFinal:
          payload?.dateFinal || state.currentRequest?.dateFinal || null,
        reportType:
          payload?.reportType || state.currentRequest?.reportType || "",
        models: payload?.models || state.currentRequest?.models || ["55", "65"],
        sources: payload?.sources || state.currentRequest?.sources || [],
        filters: payload?.filters || state.currentRequest?.filters || [],
        messages: payload?.messages || state.currentRequest?.messages || [],
        createdAt: new Date().toISOString(),
      };

      const nextContexts = [contextToSave, ...state.savedContexts].slice(0, 20);
      writeStorage(SAVED_CONTEXTS_KEY, nextContexts);
      commit("setSavedContexts", nextContexts);
      return contextToSave;
    },
    saveReportSnapshot({ commit, state }, payload) {
      if (!state.currentPreview && !state.currentReport) {
        return null;
      }

      const reportDefinition = {
        prompt: state.currentRequest?.prompt || "",
        context: state.currentRequest?.context || "",
        reportType:
          state.currentReport?.reportType ||
          state.currentPreview?.reportType ||
          state.currentRequest?.reportType ||
          "",
        dateInitial: state.currentRequest?.dateInitial || null,
        dateFinal: state.currentRequest?.dateFinal || null,
        sources: state.currentRequest?.sources || [],
        models: state.currentRequest?.models || [],
        filters: state.currentRequest?.filters || [],
        messages: state.currentRequest?.messages || [],
      };

      const layout = {
        generatedAt:
          state.currentReport?.generatedAt ||
          state.currentPreview?.generatedAt ||
          new Date().toISOString(),
        dataSourceLabel:
          state.currentReport?.dataSourceLabel ||
          state.currentPreview?.dataSourceLabel ||
          "",
        metrics: state.currentReport?.metrics || [],
        chart: state.currentReport?.chart || null,
        columns: state.currentReport?.columns || [],
      };

      const reportToSave = {
        id: Date.now(),
        name:
          payload?.name ||
          state.currentReport?.title ||
          state.currentPreview?.title ||
          `Relatorio ${state.savedReports.length + 1}`,
        preview: state.currentPreview,
        report: state.currentReport,
        request: state.currentRequest,
        definition: reportDefinition,
        layout,
        createdAt: new Date().toISOString(),
      };

      const nextReports = [reportToSave, ...state.savedReports].slice(0, 20);
      writeStorage(SAVED_REPORTS_KEY, nextReports);
      commit("setSavedReports", nextReports);
      return reportToSave;
    },
    applySavedContext({ commit }, payload) {
      if (!payload) {
        return;
      }

      commit("setCurrentRequest", {
        prompt: payload.prompt,
        context: payload.context,
        reportType: payload.reportType || "",
        dateInitial: payload.dateInitial ? new Date(payload.dateInitial) : null,
        dateFinal: payload.dateFinal ? new Date(payload.dateFinal) : null,
        models: payload.models || ["55", "65"],
        sources: payload.sources || [],
        filters: payload.filters || [],
        messages: payload.messages || [],
      });
      commit("setCurrentPreview", null);
      commit("setCurrentReport", null);
    },
    applySavedReport({ commit }, payload) {
      if (!payload) {
        return;
      }

      commit("setCurrentRequest", {
        ...(payload.request || buildDefaultRequest()),
        dateInitial: payload.request?.dateInitial
          ? new Date(payload.request.dateInitial)
          : null,
        dateFinal: payload.request?.dateFinal
          ? new Date(payload.request.dateFinal)
          : null,
      });
      commit("setCurrentPreview", payload.preview || payload.report || null);
      commit("setCurrentReport", payload.report || null);
    },
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setError(state, value) {
      state.error = value;
    },
    setCurrentRequest(state, value) {
      state.currentRequest = normalizeRequest(value);
    },
    setCurrentPreview(state, value) {
      state.currentPreview = value;
    },
    setCurrentReport(state, value) {
      state.currentReport = value;
    },
    appendDebugEvent(state, value) {
      state.debugEvents.push(value);

      if (state.debugEvents.length > 100) {
        state.debugEvents.splice(0, state.debugEvents.length - 100);
      }
    },
    setDebugEvents(state, value) {
      state.debugEvents = value || [];
    },
    setSavedContexts(state, value) {
      state.savedContexts = value || [];
    },
    setSavedReports(state, value) {
      state.savedReports = value || [];
    },
  },
};

export default assistantReports;
